import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon "Back to School '26" yazıcı kampanyası medya planı.
 * 25 Ağustos – 30 Eylül 2026 (37 gün), FB + IG.
 * Bütçe modeli Canon Nisan / Beyond The Frame planlarıyla aynı: AHK %10, Tasarım ayrı, Stopaj %15.
 *
 * CPM/CPC varsayımları uydurma DEĞİL: Canon'un kendi Meta hesabının (act_3256944827800966)
 * 2026-05-01 sonrası gerçekleşen verisinden, hedef × format bazında türetildi.
 */
export default function CanonBackToSchool2026() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe kırılımı ────────────────────────────────────────────────────────
  const TOTAL = 500000;
  const AHK_RATE = 0.10;
  const AHK_FEE = Math.round(TOTAL * AHK_RATE);        // 50.000 — Ajans Hizmet Komisyonu
  const DESIGN = 63000;                                 // Tasarım (Back to School kreatifleri)
  const MEDIA_BRUT = TOTAL - AHK_FEE - DESIGN;          // 387.000 (stopaj dahil medya)
  const STOPAJ_RATE = 0.15;
  const STOPAJ = Math.round(MEDIA_BRUT * STOPAJ_RATE);  // 58.050
  const NET = MEDIA_BRUT - STOPAJ;                      // 328.950 — Meta'da gerçek harcanan

  // ── Süre ──────────────────────────────────────────────────────────────────
  const START = '2026-08-25';
  const END = '2026-09-30';
  const DAYS = 37; // 25 Ağustos → 30 Eylül dahil
  const dailyNet = NET / DAYS;
  const FREQ = 2.8; // 37 günlük akış için tahmini ortalama frekans

  // ── Kreatif bazında bütçe ─────────────────────────────────────────────────
  // 5 kreatif, sunumdaki yayın tarihleriyle. Hedef seçimi karma:
  // ucuz erişim taşıyan statik post + video Awareness'ta, etkileşim/tık üreten
  // carousel + story Trafik'te, reel Etkileşim'de.
  const creatives = [
    {
      key: 'post',
      title: 'Instagram Post',
      headline: 'Yeni Dönem Canon İle Başlıyor',
      product: 'MegaTank G3410',
      goal: 'Awareness',
      goalKey: 'aw',
      size: '1080×1350',
      placement: 'IG/FB Feed',
      start: '2026-08-25',
      share: 0.20,
      cpm: 8.00,
      cpc: 7.63,
      img: '/canon-bts/post.jpg',
    },
    {
      key: 'carousel',
      title: 'Instagram Carousel',
      headline: 'Projelerim / Hayallerim / Baskılarım Canon İle Hazır',
      product: 'G3480 · G3410 · G3470',
      goal: 'Trafik',
      goalKey: 'tr',
      size: '1080×1350 ×3',
      placement: 'IG/FB Feed Carousel',
      start: '2026-08-25',
      share: 0.18,
      cpm: 45.00,
      cpc: 2.61,
      img: '/canon-bts/caro1.jpg',
      extraImgs: ['/canon-bts/caro2.jpg', '/canon-bts/caro3.jpg'],
    },
    {
      key: 'video',
      title: 'Video',
      headline: 'Merak Ettiğini Yazdır (4 sahne)',
      product: 'MegaTank serisi',
      goal: 'Awareness',
      goalKey: 'aw',
      size: '1080×1920',
      placement: 'IG/FB Feed + Reels',
      start: '2026-08-31',
      share: 0.24,
      cpm: 8.00,
      cpc: 16.86,
      img: '/canon-bts/video.jpg',
      isVideo: true,
    },
    {
      key: 'story',
      title: 'Instagram Story',
      headline: 'Hedeflerim Canon İle Hazır',
      product: 'MegaTank G3470',
      goal: 'Trafik',
      goalKey: 'tr',
      size: '1080×1920',
      placement: 'IG/FB Stories',
      start: '2026-09-04',
      share: 0.18,
      cpm: 42.00,
      cpc: 2.40,
      img: '/canon-bts/story.jpg',
    },
    {
      key: 'reel',
      title: 'Reel',
      headline: 'Hayallerim Canon İle Hazır',
      product: 'Akıllı tank sistemi · Wi-Fi',
      goal: 'Etkileşim',
      goalKey: 'en',
      size: '1080×1920',
      placement: 'IG/FB Reels',
      start: '2026-09-14',
      share: 0.20,
      cpm: 31.50,
      cpc: 2.41,
      img: '/canon-bts/reel.jpg',
      isVideo: true,
    },
  ];

  // Bütçe dağıt — son kalem yuvarlama farkını üstlenir, toplam NET'e birebir oturur.
  let alloc = 0;
  const rows = creatives.map((c, i) => {
    const isLast = i === creatives.length - 1;
    const budget = isLast ? NET - alloc : Math.round(NET * c.share);
    alloc += budget;
    const impressions = Math.round((budget / c.cpm) * 1000);
    const clicks = Math.round(budget / c.cpc);
    const reach = Math.round(impressions / FREQ);
    const days = Math.round((new Date(END) - new Date(c.start)) / 86400000) + 1;
    return { ...c, budget, impressions, clicks, reach, days, end: END };
  });

  const totals = rows.reduce((t, r) => ({
    budget: t.budget + r.budget,
    impressions: t.impressions + r.impressions,
    clicks: t.clicks + r.clicks,
  }), { budget: 0, impressions: 0, clicks: 0 });
  const blendedCpm = (totals.budget / totals.impressions) * 1000;
  const blendedCpc = totals.budget / totals.clicks;
  const estReach = Math.round(totals.impressions / FREQ);
  const ctr = (totals.clicks / totals.impressions) * 100;

  // Hedef bazında özet
  const goalSummary = ['Awareness', 'Trafik', 'Etkileşim'].map((g) => {
    const rs = rows.filter((r) => r.goal === g);
    const budget = rs.reduce((s, r) => s + r.budget, 0);
    const impressions = rs.reduce((s, r) => s + r.impressions, 0);
    const clicks = rs.reduce((s, r) => s + r.clicks, 0);
    return { goal: g, budget, impressions, clicks, pct: (budget / NET) * 100 };
  });

  // ── Takvim ────────────────────────────────────────────────────────────────
  const TL_START = new Date(START);
  const TL_END = new Date(END);
  const TL_SPAN = (TL_END - TL_START) / 86400000;
  const tlOff = (d) => (new Date(d) - TL_START) / 86400000;
  const tlPct = (d) => (tlOff(d) / TL_SPAN) * 100;
  const fmtDay = (d) => new Date(d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  const GOAL_TONE = { Awareness: '#CC0000', 'Trafik': '#6366f1', 'Etkileşim': '#f59e0b' };
  const tlMarks = [
    { label: '25 Ağu · Lansman', date: START },
    { label: '1 Eyl', date: '2026-09-01' },
    { label: '15 Eyl · Okul açılışı', date: '2026-09-15' },
    { label: '30 Eyl', date: END },
  ];

  // PDF: her bölüm bütün halinde basılır. Bir bölüm kalan yere sığmıyorsa
  // ortadan kesilmek yerine yeni sayfaya atılır; sadece tek başına bir sayfadan
  // uzun olan bölüm dilimlenir. Her sayfanın tepesinde Canon/Jellybon başlığı olur.
  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const root = slideRef.current;
      const headerEl = root.querySelector('[data-pdf-header]');
      const sectionEls = Array.from(root.querySelectorAll('[data-pdf-section]'));
      // html2canvas bölümü bir iframe klonunda çiziyor; genişlik sabitlenmezse
      // dipnot paragrafları fazladan satıra kayıp kutunun altından taşıyordu.
      // width/windowWidth sarmalama biçimini birebir korur, +12px pay son satırı kurtarır.
      const shoot = (el) => html2canvas(el, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        width: el.offsetWidth,
        height: el.scrollHeight + 12,
        windowWidth: document.documentElement.clientWidth,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const M = 8;          // sayfa kenar boşluğu (mm)
      const GAP = 6;        // bölümler arası boşluk (mm)
      const CW = pw - M * 2;
      const BOTTOM = ph - M - 6; // altta sayfa numarası için yer bırak

      const headerCanvas = await shoot(headerEl);
      const headerImg = headerCanvas.toDataURL('image/jpeg', 0.95);
      const headerH = (headerCanvas.height * CW) / headerCanvas.width;
      const TOP_Y = M + headerH + GAP;
      const PAGE_H = BOTTOM - TOP_Y;   // bir sayfaya sığan azami bölüm yüksekliği

      let pages = 0;
      let y = 0;
      const startPage = () => {
        if (pages > 0) pdf.addPage();
        pages += 1;
        pdf.addImage(headerImg, 'JPEG', M, M, CW, headerH);
        y = TOP_Y;
      };

      // Canvas'ın bir dilimini ayrı bir jpeg olarak çıkarır (jsPDF kırpma yapmaz).
      const slice = (canvas, srcY, srcH) => {
        const c = document.createElement('canvas');
        c.width = canvas.width;
        c.height = srcH;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        return c.toDataURL('image/jpeg', 0.95);
      };

      startPage();
      for (const el of sectionEls) {
        const canvas = await shoot(el);
        const pxPerMm = canvas.width / CW;
        let srcY = 0;
        let remain = canvas.height / pxPerMm; // mm

        while (remain > 0.5) {
          const avail = BOTTOM - y;
          // Kalan yere sığıyorsa bas ve geç.
          if (remain <= avail) {
            const srcH = Math.round(remain * pxPerMm);
            pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, remain);
            y += remain + GAP;
            remain = 0;
            break;
          }
          // Sığmıyor ama tek başına bir sayfaya sığar → bölme, yeni sayfaya at.
          if (remain <= PAGE_H) {
            startPage();
            continue;
          }
          // Gerçekten bir sayfadan uzun → dilimlemekten başka yol yok.
          const srcH = Math.round(avail * pxPerMm);
          pdf.addImage(slice(canvas, srcY, srcH), 'JPEG', M, y, CW, avail);
          srcY += srcH;
          remain -= avail;
          startPage();
        }
      }

      // Sayfa numaraları
      for (let i = 1; i <= pages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150);
        pdf.text(`Canon Back to School '26 · Jellybon x Glowytics · ${i}/${pages}`, pw / 2, ph - M + 2, { align: 'center' });
      }

      pdf.save('Canon_Back_To_School_2026_Medya_Plani.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
      amber: 'border-amber-100 bg-amber-50 text-amber-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  const GoalPill = ({ goal }) => {
    const cls = {
      Awareness: 'bg-red-50 text-red-700 border-red-100',
      'Trafik': 'bg-indigo-50 text-indigo-700 border-indigo-100',
      'Etkileşim': 'bg-amber-50 text-amber-700 border-amber-100',
    }[goal];
    return <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>{goal}</span>;
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div data-pdf-header className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">Back to School '26 — Yazıcı Kampanyası Medya Planı</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) · 25 Ağustos – 30 Eylül 2026 · 37 gün</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Kampanya detayı */}
          <div data-pdf-section className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Kampanya Detayı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon <strong>MegaTank</strong> yazıcı serisinin okula dönüş dönemi kampanyası. Yeni eğitim yılı hazırlık
              döneminde (25 Ağustos) başlayıp okul açılışını (15 Eylül) kapsayarak Eylül sonuna kadar sürer.
              Ana mesaj: <strong>“… Canon ile hazır”</strong>. Öne çıkan ürünler G3410, G3470 ve G3480; iletişimin
              omurgası kesintisiz baskı performansı, yüksek mürekkep kapasitesi ve Wi-Fi kolaylığı.
              Kampanya hashtag'i <strong>#CanonileHazır</strong>.
            </p>
            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
              5 kreatif: 1 statik post, 1 carousel (3 sayfa), 1 video, 1 story, 1 reel. Tamamı FB + IG,
              label <span className="font-mono">NSO_TURKEY / FUTURE OF WHAT'S POSSIBLE</span>.
            </p>
          </div>

          {/* Bütçe kırılımı */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Toplam Bütçe" value={fmtCur(TOTAL)} sub="müşteri toplam" />
              <Stat label="AHK (Ajans %10)" value={'-' + fmtCur(AHK_FEE)} sub={`${fmtCur(TOTAL)} × 0,10`} tone="indigo" />
              <Stat label="Tasarım" value={'-' + fmtCur(DESIGN)} sub="5 kreatif üretimi" tone="indigo" />
              <Stat label="Stopaj (%15)" value={'-' + fmtCur(STOPAJ)} sub={`${fmtCur(MEDIA_BRUT)} × 0,15`} tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Günlük ortalama net harcama ≈ {fmtCur(dailyNet)} ({DAYS} gün). AHK + Tasarım + Stopaj toplam
              {' '}{fmtCur(TOTAL)}'ye dahildir. Brüt medya {fmtCur(MEDIA_BRUT)}.
            </p>
          </div>

          {/* Hedef dağılımı */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Reklam Hedefi Dağılımı</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Karma kurgu: erişim maliyeti düşük formatlar (statik post, video) Awareness'ta, tık ve etkileşim
              üreten formatlar (carousel, story, reel) Trafik / Etkileşim'de. Tek hedefte toplanırsa ya erişim
              ya tık tarafı çöküyor.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {goalSummary.map((g) => (
                <div key={g.goal} className="rounded-xl border border-gray-200 p-3">
                  <div className="flex items-center justify-between">
                    <GoalPill goal={g.goal} />
                    <span className="text-[10px] font-bold text-gray-500 tabular-nums">%{g.pct.toFixed(0)}</span>
                  </div>
                  <p className="text-lg font-bold text-gray-800 mt-1.5 tabular-nums">{fmtCur(g.budget)}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5 tabular-nums">
                    {fmtInt(g.impressions)} gösterim · {fmtInt(g.clicks)} tık
                  </p>
                  <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${g.pct}%`, background: GOAL_TONE[g.goal] }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Kreatif bazında plan tablosu */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">Kreatif Bazında Bütçe ve Projeksiyon (5 İçerik)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">İçerik</th>
                    <th className="text-left px-3 py-2 font-semibold">Hedef</th>
                    <th className="text-left px-3 py-2 font-semibold">Yayın</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe (NET)</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim*</th>
                    <th className="text-right px-3 py-2 font-semibold">Tıklama</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                    <th className="text-right px-3 py-2 font-semibold">CPC</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.key} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <p className="font-semibold text-gray-800">{r.title}</p>
                        <p className="text-[9px] text-gray-400">{r.product}</p>
                        <p className="text-[9px] text-gray-300">{r.size} · {r.placement}</p>
                      </td>
                      <td className="px-3 py-2"><GoalPill goal={r.goal} /></td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        {fmtDay(r.start)} → {fmtDay(r.end)}
                        <span className="text-gray-300"> · {r.days}g</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">
                        {fmtCur(r.budget)}
                        <span className="block text-[9px] font-normal text-gray-400">%{Math.round((r.budget / NET) * 100)}</span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.reach)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.clicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{r.cpm.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">₺{r.cpc.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(estReach)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInt(totals.clicks)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpm.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">₺{blendedCpc.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              *Erişim, ~{FREQ} ortalama frekans varsayımıyla tahminidir. Tahmini CTR ≈ %{ctr.toFixed(2)}.
              CPM ve CPC değerleri Canon Meta hesabının 1 Mayıs 2026 sonrası <strong>gerçekleşen</strong> verisinden,
              hedef × format bazında alınmıştır. Meta açık artırma dinamiklerine göre değişebilir.
            </p>
          </div>

          {/* Projeksiyon özeti */}
          <div data-pdf-section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Toplam Gösterim" value={fmtInt(totals.impressions)} sub="tahmini" tone="red" />
            <Stat label="Tahmini Erişim" value={fmtInt(estReach)} sub={`~${FREQ} frekans`} tone="green" />
            <Stat label="Toplam Tıklama" value={fmtInt(totals.clicks)} sub="link tıklaması (tahmini)" />
            <Stat label="Ortalama CPM" value={'₺' + blendedCpm.toFixed(2)} sub={`CPC ₺${blendedCpc.toFixed(2)} · CTR %${ctr.toFixed(2)}`} />
          </div>

          {/* Yayın takvimi */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Yayın Takvimi</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Kreatifler kademeli devreye girer, hepsi 30 Eylül'de kapanır. Lansman 25 Ağustos'ta post + carousel ile,
              okul açılışı haftası story ile, kapanış dalgası reel ile karşılanır.
            </p>
            <div className="rounded-xl border border-gray-200 p-4 overflow-hidden">
              {/* Tarih başlıkları */}
              <div className="flex">
                <div className="w-40 md:w-56 shrink-0" />
                <div className="relative flex-1 h-4">
                  {tlMarks.map((m, i) => (
                    <span
                      key={m.label}
                      className="absolute top-0 text-[9px] font-semibold text-gray-400 whitespace-nowrap"
                      style={{
                        left: `${tlPct(m.date)}%`,
                        transform: i === 0 ? 'none' : i === tlMarks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                      }}
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="relative mt-1.5">
                {/* dikey çizgiler */}
                <div className="absolute inset-0 flex pointer-events-none">
                  <div className="w-40 md:w-56 shrink-0" />
                  <div className="relative flex-1">
                    {tlMarks.map((m) => (
                      <div key={m.label} className="absolute top-0 bottom-0 w-px bg-gray-100" style={{ left: `${tlPct(m.date)}%` }} />
                    ))}
                  </div>
                </div>

                {rows.map((r) => {
                  const left = tlPct(r.start);
                  const width = Math.max(tlPct(r.end) - left, 2);
                  const nearEnd = left > 60;
                  return (
                    <div key={r.key} className="flex items-center h-12 relative">
                      <div className="w-40 md:w-56 shrink-0 pr-3">
                        <p className="text-[11px] font-semibold text-gray-800 whitespace-nowrap" style={{ lineHeight: '1.5' }}>{r.title}</p>
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-[9px] text-gray-400">{r.goal}</span>
                          <span className="text-[9px] font-bold tabular-nums" style={{ color: GOAL_TONE[r.goal] }}>
                            {fmtCur(r.budget)} · %{Math.round((r.budget / NET) * 100)}
                          </span>
                        </div>
                      </div>
                      <div className="relative flex-1 h-12">
                        <div className="absolute top-1/2 -translate-y-1/2" style={{ left: `${left}%`, width: `${width}%` }}>
                          <div className={`absolute bottom-full mb-1 whitespace-nowrap text-[8.5px] font-medium text-gray-500 ${nearEnd ? 'right-0' : 'left-0'}`}>
                            {fmtDay(r.start)} → {fmtDay(r.end)} · {r.days}g
                          </div>
                          <div className="h-4 rounded-full shadow-sm" style={{ background: GOAL_TONE[r.goal] }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-3">
                  {Object.entries(GOAL_TONE).map(([l, c]) => (
                    <span key={l} className="flex items-center gap-1.5 text-[9px] text-gray-500">
                      <span className="w-3 h-3 rounded-full" style={{ background: c }} />{l}
                    </span>
                  ))}
                </div>
                <span className="text-[9px] text-gray-400">
                  Toplam paid: <strong className="text-gray-600">{fmtCur(NET)}</strong> · {DAYS} gün
                </span>
              </div>
            </div>
          </div>

          {/* Kreatifler */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-1">Kreatifler</p>
            <p className="text-[10px] text-gray-400 mb-3">
              Sunumdaki 5 içerik. Carousel 3 sayfadan oluşur (G3480 · G3410 · G3470).
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {rows.map((r) => (
                <div key={r.key} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                  <div className="relative bg-gray-50">
                    <img src={r.img} alt={r.title} className="w-full h-auto object-cover" style={{ aspectRatio: '4 / 5', objectPosition: 'top' }} />
                    {r.isVideo && (
                      <span className="absolute top-1.5 right-1.5 text-[8px] font-bold text-white bg-black/60 px-1.5 py-0.5 rounded">
                        VİDEO
                      </span>
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className="flex items-center justify-between gap-1">
                      <p className="text-[10px] font-bold text-gray-800">{r.title}</p>
                      <span className="text-[9px] text-gray-400 tabular-nums">{fmtDay(r.start)}</span>
                    </div>
                    <p className="text-[9px] text-gray-500 mt-0.5 leading-snug">{r.headline}</p>
                    <div className="flex items-center justify-between mt-1.5">
                      <GoalPill goal={r.goal} />
                      <span className="text-[9px] font-bold tabular-nums" style={{ color: GOAL_TONE[r.goal] }}>{fmtCur(r.budget)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Carousel diğer sayfaları */}
            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Carousel — 2. ve 3. Sayfa</span>
              <div className="flex gap-3 mt-2">
                {['/canon-bts/caro2.jpg', '/canon-bts/caro3.jpg'].map((src, i) => (
                  <div key={src} className="w-28">
                    <img src={src} alt={`Carousel sayfa ${i + 2}`} className="w-full rounded-lg border border-gray-100" />
                    <p className="text-[9px] text-gray-400 mt-1 text-center">Sayfa {i + 2}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Hedefleme */}
          <div data-pdf-section>
            <p className="text-sm font-bold text-gray-800 mb-2">Hedefleme</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Birincil Kitle" value="Ebeveynler" sub="okul çağında çocuğu olan" tone="red" />
              <Stat label="İkincil Kitle" value="Öğrenciler" sub="lise son + üniversite" />
              <Stat label="Yaş / Cinsiyet" value="25–54" sub="Tüm cinsiyetler · kadın ağırlıklı" />
              <Stat label="Platform" value="FB + IG" sub="Meta Ads · otomatik yerleşim" />
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Lokasyon</span>
              <p className="text-xs text-gray-700 mt-1 leading-relaxed">
                <strong>Tüm Türkiye</strong> — büyükşehir ağırlıklı (İstanbul, Ankara, İzmir, Bursa, Antalya, Adana,
                Konya). Üniversite şehirleri ek ağırlıkla hedeflenir. Awareness kırılımında kapsam geniş bırakılır,
                Trafik kırılımında büyükşehir yoğunlaştırılır.
              </p>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">İlgi Alanları (Detaylı Hedefleme)</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['Ebeveynlik', 'Okul öncesi & ilkokul velisi', 'Eğitim', 'Kırtasiye & okul malzemeleri', 'Ev ofis',
                  'Yazıcı & ofis ekipmanları', 'Teknoloji ürünleri', 'Online alışveriş', 'Üniversite öğrencileri',
                  'Uzaktan eğitim', 'Fotoğraf baskısı', 'DIY & el işi projeleri', 'Çocuk gelişimi'].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-100">{t}</span>
                ))}
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 p-3">
              <span className="text-[10px] font-medium uppercase text-gray-500">Davranışlar & Demografi Detayı</span>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {['Ebeveyn (6–12 yaş çocuk)', 'Ebeveyn (13–18 yaş çocuk)', 'Yeni okula başlayan çocuk velisi',
                  'Teknoloji erken benimseyenler', 'Etkin online alışveriş yapanlar'].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{t}</span>
                ))}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                <span className="text-[10px] font-medium uppercase text-emerald-700">Özel Kitleler (Custom Audience)</span>
                <ul className="text-[11px] text-gray-700 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
                  <li>Son 365 gün IG/FB sayfa & post etkileşimcileri</li>
                  <li>Video izleyiciler (%25+ izleme) — video ve reel kreatiflerinden beslenir</li>
                  <li>canon.com.tr MegaTank / yazıcı sayfası ziyaretçileri (Pixel) — retargeting</li>
                </ul>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                <span className="text-[10px] font-medium uppercase text-emerald-700">Benzer Kitleler (Lookalike)</span>
                <ul className="text-[11px] text-gray-700 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
                  <li>Etkileşimciler LAL %1–3 (TR)</li>
                  <li>Video izleyiciler LAL %2 (TR)</li>
                  <li>Ürün sayfası ziyaretçileri LAL %1–5 (TR)</li>
                </ul>
              </div>
            </div>

            <p className="text-[10px] text-gray-400 mt-2">
              Awareness kırılımı geniş kitleyle açılır (detaylı hedefleme genişletme açık). Trafik ve Etkileşim
              kırılımlarında ilk hafta sonunda video izleyici ve etkileşimci havuzları oluşur; Eylül'de bu havuzlar
              retargeting olarak devreye alınır.
            </p>
          </div>

          {/* Benchmark dayanağı */}
          <div data-pdf-section className="rounded-xl border border-amber-100 bg-amber-50 p-4">
            <p className="text-sm font-bold text-gray-800">Projeksiyonun Dayanağı</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Bu plandaki CPM ve CPC değerleri sektör ortalaması değil, <strong>Canon'un kendi Meta hesabında
              1 Mayıs 2026 sonrası gerçekleşen</strong> rakamlardır. Hedef × format bazında ölçülen değerler:
            </p>
            <div className="overflow-x-auto rounded-lg border border-amber-200 bg-white mt-2">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-amber-50/60 text-gray-500 uppercase text-[9px]">
                    <th className="text-left px-3 py-1.5 font-semibold">Hedef × Format</th>
                    <th className="text-right px-3 py-1.5 font-semibold">Harcama</th>
                    <th className="text-right px-3 py-1.5 font-semibold">CPM</th>
                    <th className="text-right px-3 py-1.5 font-semibold">CPC</th>
                    <th className="text-right px-3 py-1.5 font-semibold">CTR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100">
                  {[
                    ['Awareness × Statik Post', 79054, 7.91, 7.63, 0.10],
                    ['Awareness × Video', 49084, 8.03, 16.86, 0.05],
                    ['Trafik × Story', 136078, 41.81, 2.40, 1.74],
                    ['Trafik × Carousel', 68974, 45.13, 2.61, 1.73],
                    ['Etkileşim × Reels', 15065, 31.37, 2.41, 1.30],
                  ].map(([n, s, cpm, cpc, ct]) => (
                    <tr key={n}>
                      <td className="px-3 py-1.5 font-medium text-gray-700">{n}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{fmtCur(s)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">₺{cpm.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">₺{cpc.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">%{ct.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 leading-relaxed">
              Hedef seçimi tek başına maliyeti <strong>4–5 kat</strong> değiştiriyor: aynı bütçe Awareness'ta
              ₺8 CPM ile geniş erişim, Trafik'te ₺36 CPM ile yüksek tık üretiyor. Bu yüzden plan tek hedefte
              toplanmadı, format bazında ayrıştırıldı.
            </p>
          </div>

          {/* Zamanlama */}
          <div data-pdf-section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-bold text-gray-800">Zamanlama & Optimizasyon</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Yayın: <strong>25 Ağustos – 30 Eylül 2026</strong> ({DAYS} gün), kesintisiz. Lansman haftasında post
              ve carousel birlikte açılır; 31 Ağustos'ta video, 4 Eylül'de story, 14 Eylül'de reel devreye girer.
              Okul açılışı haftası (15 Eylül) bütçe yoğunluğunun zirvesidir. İlk 7 gün öğrenme fazı sonunda
              format ve yerleşim ağırlıkları performansa göre yeniden dağıtılır; düşük performanslı kırılımdan
              yüksek performanslıya bütçe kaydırılır.
            </p>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              {new Date().toLocaleDateString('tr-TR')} · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
